/**
 * InsightCard — KI-Insight card with confidence badge + action row.
 * Ported from v0 components/brokly/insight-card.tsx.
 * Simplified: no sonner, no external DecisionBar — inline action buttons.
 * Confidence colours: ≥90% ok/green, 70–89% info/blue, <70% decide/orange.
 */
import { Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { cn } from '../../../lib/cn';
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '../../../components/ui/Badge';

export interface InsightAction<A extends string = string> {
  action: A;
  label: string;
  icon: LucideIcon;
  /** Button variant */
  variant?: 'default' | 'outline' | 'ghost' | 'secondary';
  /** Push to right end */
  pushEnd?: boolean;
}

function konfidenzColor(k: number): { text: string; badgeVariant: BadgeVariant } {
  if (k >= 90) return { text: 'text-sem-ok', badgeVariant: 'ok' };
  if (k >= 70) return { text: 'text-sem-info', badgeVariant: 'info' };
  return { text: 'text-sem-decide', badgeVariant: 'decide' };
}

interface InsightCardProps<A extends string = string> {
  title: string;
  /** Optional soft category badge (e.g. Chance/Risiko) rendered after the title. */
  tag?: ReactNode;
  konfidenz?: number;
  actions: InsightAction<A>[];
  onAction: (action: A) => void;
  children: ReactNode;
  className?: string;
}

export function InsightCard<A extends string = string>({
  title,
  tag,
  konfidenz,
  actions,
  onAction,
  children,
  className,
}: InsightCardProps<A>) {
  const [feedback, setFeedback] = useState<string | null>(null);
  const accent = konfidenz !== undefined ? konfidenzColor(konfidenz) : { text: 'text-sem-info', badgeVariant: 'info' as BadgeVariant };

  function handleAction(action: A, label: string) {
    onAction(action);
    setFeedback(label);
    setTimeout(() => setFeedback(null), 2000);
  }

  return (
    <Card className={cn('overflow-hidden', className)}>
      <div className="p-4">
        <div className="flex items-center gap-2 text-sm font-medium text-foreground">
          <Sparkles className={cn('size-4', accent.text)} aria-hidden />
          {title}
          {tag}
          {konfidenz !== undefined && (
            <Badge variant={accent.badgeVariant} className="ml-auto text-[10px]">
              Konfidenz {konfidenz}%
            </Badge>
          )}
        </div>
        <div className="mt-2 text-sm leading-relaxed text-foreground/80">{children}</div>
        {feedback && (
          <div
            role="status"
            aria-live="polite"
            className="mt-2 text-xs text-muted-foreground"
          >
            {feedback}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 border-t bg-muted/30 px-4 py-2">
        {actions.map((a) => {
          const Icon = a.icon;
          return (
            <Button
              key={a.action}
              variant={a.variant ?? 'ghost'}
              size="sm"
              className={cn('h-7 text-xs', a.pushEnd && 'ml-auto')}
              onClick={() => handleAction(a.action, a.label)}
            >
              <Icon className="size-3.5" />
              {a.label}
            </Button>
          );
        })}
      </div>
    </Card>
  );
}
